import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  accentS: 'rgba(217,119,87,0.10)',
  accentB: 'rgba(217,119,87,0.25)',
  green: '#4CAF82',
  text: '#F0EDE8',
  muted: '#888',
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; color: ${C.text}; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
  @keyframes spin { to { transform: rotate(360deg) } }
  input { color-scheme: dark; font-family: 'Inter', sans-serif; }
`;

function Spinner() {
  return <div style={{ width: 16, height: 16, border: '1.5px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .7s linear infinite' }} />;
}

// Helper to format FastAPI error responses
function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const inp = {
    width: '100%',
    padding: '10px 13px',
    background: C.surf,
    border: `0.5px solid ${C.bdr}`,
    borderRadius: 8,
    color: C.text,
    fontSize: 15.5,
    outline: 'none',
    transition: 'border-color .15s',
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login' ? { email, password } : { email, password, name };

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMsg;
        if (response.status === 401 && mode === 'login') {
          // Ingress may strip the 401 body, hardcode the only possible 401 reason
          errorMsg = 'Invalid email or password';
        } else {
          // Read the body once as text — works whether it's JSON or HTML
          const rawText = await response.text().catch(() => '');
          let parsed = null;
          try {
            parsed = rawText ? JSON.parse(rawText) : null;
          } catch (_) {
            // Body is not JSON (likely an ingress / gateway HTML error page)
          }
          if (parsed && parsed.detail != null) {
            errorMsg = formatApiError(parsed.detail);
          } else {
            // Surface the status code so the user (and we) can debug
            errorMsg = `Login failed (HTTP ${response.status}). Please try again or use 'Forgot password' below.`;
            // Log the raw response for diagnostic — visible in browser DevTools
            console.error('[auth] non-JSON error response', { status: response.status, body: rawText?.slice(0, 500) });
          }
        }
        throw new Error(errorMsg);
      }

      const user = await response.json();
      navigate('/', { replace: true, state: { user } });
    } catch (err) {
      // Network / CORS / parse errors land here too — surface the actual message
      setError(err.message || 'Network error. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 400, animation: 'fadeUp .4s ease' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 36, justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>C</div>
            <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.5px' }}>
              Content<span style={{ color: C.accent }}>Gap</span>
            </span>
          </div>

          {/* Card */}
          <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 12, padding: '28px 26px' }}>
            <h1 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-.5px', marginBottom: 4 }} data-testid="auth-heading">
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p style={{ fontSize: 15, color: C.muted, marginBottom: 24 }}>
              {mode === 'login' ? 'Sign in to your ContentGap workspace.' : 'Start closing your content gaps today.'}
            </p>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit}>
              {mode === 'signup' && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>Name (optional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    style={inp}
                    data-testid="signup-name-input"
                    onFocus={(e) => (e.target.style.borderColor = C.accent)}
                    onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                  />
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={inp}
                  data-testid="auth-email-input"
                  onFocus={(e) => (e.target.style.borderColor = C.accent)}
                  onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                  <label style={{ fontSize: 13, color: C.muted }}>Password</label>
                  {mode === 'login' && (
                    <Link
                      to="/forgot-password"
                      data-testid="forgot-password-link"
                      style={{ fontSize: 12, color: C.accent, textDecoration: 'none', fontWeight: 500 }}
                    >
                      Forgot password?
                    </Link>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signup' ? 'Min. 6 characters' : '••••••••'}
                    style={{ ...inp, paddingRight: 38 }}
                    data-testid="auth-password-input"
                    onFocus={(e) => (e.target.style.borderColor = C.accent)}
                    onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-visibility"
                    style={{
                      position: 'absolute',
                      right: 9,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: C.muted,
                      cursor: 'pointer',
                      padding: 5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 4,
                      transition: 'color .15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = C.text)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
                  >
                    {showPassword ? <EyeOff size={16} strokeWidth={1.8} /> : <Eye size={16} strokeWidth={1.8} />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  data-testid="auth-error"
                  style={{
                    background: 'rgba(220,60,60,0.10)',
                    border: '0.5px solid rgba(220,60,60,0.3)',
                    borderRadius: 7,
                    padding: '9px 13px',
                    fontSize: 14.5,
                    color: '#FF7070',
                    marginBottom: 14,
                  }}
                >
                  <div>{error}</div>
                  {mode === 'login' && (
                    <Link
                      to="/forgot-password"
                      data-testid="error-forgot-password-link"
                      style={{ display: 'inline-block', marginTop: 6, fontSize: 13, color: C.accent, textDecoration: 'underline', fontWeight: 500 }}
                    >
                      Reset your password →
                    </Link>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                style={{
                  width: '100%',
                  padding: '11px',
                  background: C.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 15.5,
                  fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.8 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {loading ? (
                  <>
                    <Spinner /> {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : mode === 'login' ? (
                  'Sign in'
                ) : (
                  'Create account'
                )}
              </button>
            </form>
          </div>

          {/* Toggle mode */}
          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 15, color: C.muted }}>
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => {
                setMode(mode === 'login' ? 'signup' : 'login');
                setError('');
              }}
              data-testid="auth-mode-toggle"
              style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 15, fontWeight: 500 }}
            >
              {mode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </p>

          <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: '#444' }}>
            Powered by ContentGap
          </p>
        </div>
      </div>
    </>
  );
}
